import {
  PayloadAction,
  SerializedError,
  createSlice,
  isRejectedWithValue,
} from "@reduxjs/toolkit";
import { ToastUtil } from "../../utils/ToastUtil";
import { IProductResponseView } from "../../modules/products/models/IProductResponseView";
import * as ProductActions from "./products.actions";

export const productFeatureKey = "productFeature";

export interface InitialState {
  loading: boolean;
  errorMessage: SerializedError;
  products: IProductResponseView[];
  product: IProductResponseView;
  searchFilteredProducts: IProductResponseView[];
  searchQuery: string;
}

const initialState: InitialState = {
  loading: false,
  errorMessage: {} as SerializedError,
  products: [] as IProductResponseView[],
  product: {} as IProductResponseView,
  searchFilteredProducts: [],
  searchQuery: "",
};

export const productSlice = createSlice({
  name: "productSlice",
  initialState: initialState,
  reducers: {
    setSearchFilteredProducts: (
      state,
      action: PayloadAction<IProductResponseView[]>
    ) => {
      state.searchFilteredProducts = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
      state.searchFilteredProducts = state.products.filter(
        (product) =>
          product._id?.toLowerCase().includes(action.payload.toLowerCase()) ||
          product.title.toLowerCase().includes(action.payload.toLowerCase()) ||
          product.brand.toLowerCase().includes(action.payload.toLowerCase()) ||
          product.categoryObj.name
            .toLowerCase()
            .includes(action.payload.toLowerCase()) ||
          product.subCategoryObj.name
            .toLowerCase()
            .includes(action.payload.toLowerCase())
      );
    },
  },
  extraReducers: (builder) => {
    // Create a Product
    builder
      .addCase(ProductActions.createProductAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(
        ProductActions.createProductAction.fulfilled,
        (state, action) => {
          state.loading = false;
          state.product = action.payload.data;
          ToastUtil.displaySuccessToast(action.payload.msg);
        }
      )
      .addCase(ProductActions.createProductAction.rejected, (state, action) => {
        state.loading = false;
        ToastUtil.displayErrorToast("Product Creation is Failed!");
        if (isRejectedWithValue(action)) {
          state.errorMessage = action.error;
        }
      });

    // Update Product
    builder
      .addCase(ProductActions.udpateProductAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(
        ProductActions.udpateProductAction.fulfilled,
        (state, action) => {
          state.loading = false;
          state.product = action.payload.data;
          ToastUtil.displaySuccessToast(action.payload.msg);
        }
      )
      .addCase(ProductActions.udpateProductAction.rejected, (state, action) => {
        state.loading = false;
        ToastUtil.displayErrorToast("Product Update is Failed!");
        if (isRejectedWithValue(action)) {
          state.errorMessage = action.error;
        }
      });

    // Get All Products
    builder
      .addCase(ProductActions.getAllProductsAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(
        ProductActions.getAllProductsAction.fulfilled,
        (state, action) => {
          state.loading = false;
          state.products = action.payload.data;
        }
      )
      .addCase(
        ProductActions.getAllProductsAction.rejected,
        (state, action) => {
          state.loading = false;
          ToastUtil.displayErrorToast("Getting All Products is Failed!");
          if (isRejectedWithValue(action)) {
            state.errorMessage = action.error;
          }
        }
      );

    // Get A Product
    builder
      .addCase(ProductActions.getProductAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(ProductActions.getProductAction.fulfilled, (state, action) => {
        state.loading = false;
        state.product = action.payload.data;
      })
      .addCase(ProductActions.getProductAction.rejected, (state, action) => {
        state.loading = false;
        ToastUtil.displayErrorToast("Getting Product is Failed!");
        if (isRejectedWithValue(action)) {
          state.errorMessage = action.error;
        }
      });

    // Delete A Product
    builder
      .addCase(ProductActions.deleteProductAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(
        ProductActions.deleteProductAction.fulfilled,
        (state, action) => {
          state.loading = false;
          state.product = action.payload.data;
          ToastUtil.displaySuccessToast(action.payload.msg);
        }
      )
      .addCase(ProductActions.deleteProductAction.rejected, (state, action) => {
        state.loading = false;
        ToastUtil.displayErrorToast("Product Delete is Failed!");
        if (isRejectedWithValue(action)) {
          state.errorMessage = action.error;
        }
      });

    // Get All Products With Category ID
    builder
      .addCase(
        ProductActions.getAllProductsWithCategoryIdAction.pending,
        (state, action) => {
          state.loading = true;
        }
      )
      .addCase(
        ProductActions.getAllProductsWithCategoryIdAction.fulfilled,
        (state, action) => {
          state.loading = false;
          state.products = action.payload.data;
        }
      )
      .addCase(
        ProductActions.getAllProductsWithCategoryIdAction.rejected,
        (state, action) => {
          state.loading = false;
          ToastUtil.displayErrorToast(
            "Getting All Products With Cat-ID is Failed!"
          );
          if (isRejectedWithValue(action)) {
            state.errorMessage = action.error;
          }
        }
      );
  },
});

export const { setSearchFilteredProducts, setSearchQuery } = productSlice.actions;

export default productSlice.reducer;
